import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import { useFormik } from 'formik';
import { SaveJobInitialValues, saveJobSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import ReactSelect from 'react-select';
import { SaveJobInterface } from 'constants/interfaces';
import toast from 'react-hot-toast';
import { useIndustriesListQuery, useSaveJobAlertMutation } from 'services';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import Loader from 'components/Common/Loader';
import useCommonStore from 'store/common';

const JobAlert: React.FC<any> = () => {
	const { translate } = useTranslate();
	const { data: { industries: industrieslist = [] } = {} } = useIndustriesListQuery();
	const { mutateAsync: saveJobAlert, isPending } = useSaveJobAlertMutation();
	const { hideCommonModal } = useCommonStore((state) => state);

	const formik: any = useFormik({
		initialValues: SaveJobInitialValues,
		validationSchema: saveJobSchema(translate),
		enableReinitialize: true,
		onSubmit: (values: any) => {
			saveJobAlert({ ...values })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => hideCommonModal());
		},
	});

	const renderError = <T extends keyof SaveJobInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => hideCommonModal()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Modal.saveJobAlert')}</h4>
					<Row>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Modal.jobTitle')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.addJobTitle')}
									className="rounded-pill"
									name="title"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.title}
									autoComplete="off"
								/>
								{renderError('title')}
							</Form.Group>
						</Col>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Modal.industry')}</Form.Label>
								<ReactSelect
									isSearchable
									name="industryId"
									options={industrieslist}
									getOptionValue={(option) => option.id}
									getOptionLabel={(option) => option.title}
									onChange={(optionValue: any) =>
										formik.setFieldValue('industryId', optionValue?.id)
									}
									onBlur={formik.handleBlur}
									autoFocus={false}
									placeholder={translate('PlaceHolder.selectIndustry')}
									className="form-react-select "
									classNamePrefix="select"
									value={industrieslist?.find(
										(item: any) => item.id === Number(formik.values.industryId),
									)}
								/>
								{renderError('industryId')}
							</Form.Group>
						</Col>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">Location</Form.Label>
								<CustomSearchAddress
									selectProps={{
										placeholder: translate('PlaceHolder.cityStateCountry'),
										onChange: (value: any) => {
											formik.setFieldValue('location', value.location);
											formik.setFieldValue('city', value.cityId);
											formik.setFieldValue('state', value.stateId);
											formik.setFieldValue('country', value.countryId);
										},
										name: 'location',
									}}
									fieldValue={formik.values.location?.[0]}
									onBlur={formik.handleBlur}
								/>
								{renderError('location')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						{isPending ? (
							<Loader />
						) : (
							<Button className="filled-btn rounded-pill" type="submit">
								{translate('Modal.save')}
							</Button>
						)}
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default JobAlert;
